import {AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LessonType } from '../../model/calendar';
import {FilterDefinition} from "../../../model/CourseFilters";
import { CourseFilterComponent } from '../../../utils/course-filter/course-filter.component';
import {Page, Pageable} from "../../../utils/pageable";
import {map, merge, Observable, of, Subscription, switchMap, tap} from "rxjs";
import * as LessonsV3 from "../../model/v3/lessons";
import {SpinnerService} from "../../../utils/services/spinner.service";
import { WebSocketService } from '../../services/web-socket.service';
import { environment } from 'src/environments/environment';
import {NativeServiceApiProvider} from "../../../services/native-api-provider.service";

@Component({
    selector: 'app-lessons-page',
    templateUrl: './lessons-page.component.html',
    styleUrls: ['./lessons-page.component.scss'],
    standalone: false
})
export class LessonsPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly futureLessonStatuses: LessonsV3.LessonStatus[] = [
    LessonsV3.LessonStatus.Booked,
    LessonsV3.LessonStatus.Due,
    LessonsV3.LessonStatus.InProgress,
    LessonsV3.LessonStatus.Cancel,
    LessonsV3.LessonStatus.Complete,
    LessonsV3.LessonStatus.InvalidatedByStudent,
    LessonsV3.LessonStatus.InvalidatedByTeacher,
  ];
  private readonly futureLessonsPageable = new Pageable(0, 10, ['metric.details.plannedStartDate,ASC']);

  _chosenCourse: FilterDefinition;
  set chosenCourse(lang: FilterDefinition) {
    this._chosenCourse = lang;
    this.reloadFutureLessons();
  }
  get chosenCourse() {
    return this._chosenCourse;
  }

  studentId: number;
  lessonTypes = LessonType;
  CourseFilterComponent = CourseFilterComponent;
  lessons: Page<LessonsV3.LFStudentGeneral>;
  wsSubscription: Subscription;

  constructor(private route: ActivatedRoute,
              private spinner: SpinnerService,
              private provider: NativeServiceApiProvider,
              private cdr: ChangeDetectorRef,
              private webSocket: WebSocketService) {}

  ngOnInit(): void {
    this.route.paramMap.pipe(
      tap((params) => {
        this.studentId = Number(params.get('studentId'));
      }),
      switchMap(_ => this.getFutureLessons()),
      tap(() => this.startWebSocketListener())
    ).subscribe();
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.wsSubscription?.unsubscribe();
  }

  private reloadFutureLessons(): void {
    this.getFutureLessons().subscribe();
  }

  getFutureLessons(): Observable<Page<LessonsV3.LFStudentGeneral>> {
    if (!this.studentId || !this._chosenCourse) {
      return of(null);
    }

    const dateFrom = new Date(Date.now() - 5 * 60 * 1000);
    const productCode = this._chosenCourse.filter?.trim() ? this._chosenCourse.filter : null;

    return this.spinner.trace<Page<LessonsV3.LFStudentGeneral>>(
      this.provider.colV3().pipe(
        switchMap((api) =>
          api.findLessons(
            LessonsV3.LessonCategory.Col,
            null,
            'student-full',
            null,
            this.studentId,
            null,
            null,
            null,
            productCode,
            this.futureLessonStatuses,
            dateFrom,
            null,
            this.futureLessonsPageable
          )
        ),
        map((lessons) => this.asStudentGeneralPage(lessons)),
        tap((lessons) => (this.lessons = lessons)),
      )
    );
  }

  private asStudentGeneralPage(
    page: Page<LessonsV3.LessonView>
  ): Page<LessonsV3.LFStudentGeneral> {
    if (!page) {
      return null;
    }
    return {
      ...page,
      content: (page.content ?? []) as LessonsV3.LFStudentGeneral[],
    };
  }

  private startWebSocketListener(): void {
    if (!this.studentId || this.wsSubscription) {
      return;
    }

    this.wsSubscription = this.webSocket.establish(environment.eventsWsEndpoint).pipe(
        switchMap((connection) => merge(
          connection.subscribe(`/col/student/${this.studentId}/LessonListUpdate`),
          connection.subscribe(`/col/student/${this.studentId}/LessonUpdate`)
        )),
        switchMap(() => this.getFutureLessons())
      ).subscribe();
  }
}
